import { useLazyQuery } from '@apollo/client';
import { Empty, Result, Tabs } from 'antd';
import clsx from 'clsx';
import { includes, map } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../AppContext';
import {
  ACCESS_TYPE,
  BREAKPOINTS,
  ROUTES,
  TAB_KEYS,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import EllipsisText from '../../../../../../components/EllipsisText';
import SearchComponent from '../../../../../../components/SearchComponent';
import { REGISTER_LIST } from '../../../../graphql/Queries';
import AddRegisterModal from './AddRegisterModal';
import RegisterDetails from './registerDetails/RegisterDetails';

const Register = () => {
  const {
    navigate,
    params: { projectId, registerId },
    location: { state },
  } = useRouter();

  const initialFilter = {
    limit: 50,
    skip: 0,
    search: null,
  };

  const registerNavFilter = state?.registerNavFilter;
  const { getCurrentUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const [showModal, setShowModal] = useState(false);
  const [registerData, setRegisterData] = useState([]);
  const [registerFilter, setRegisterFilter] = useState(
    registerNavFilter || initialFilter,
  );
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const observerRef = useRef(null);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const [getAllRegisters, { loading }] = useLazyQuery(REGISTER_LIST, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted(res) {
      const records = res?.registerList?.data;
      setTotalCount(res?.registerList?.total);
      navigate(
        state?.currentPath ||
          `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${state?.registerId || registerId || res?.registerList?.data[0]?.id}`,
        {
          state: {
            currentPath: state?.currentPath,
            registerNavFilter: registerFilter,
          },
        },
      );
      if (scrollFlag) {
        const registerList = [...registerData, ...records];
        setRegisterData(registerList);
        setScrollFlag(false);
      } else {
        const registerList = [...records];
        setRegisterData(registerList);
      }
      setHasMore(!!records?.length);
    },
  });

  useEffect(() => {
    if (
      includes(currentUser?.tenantUser?.tenant?.access, ACCESS_TYPE.REGISTER)
    ) {
      getAllRegisters({
        variables: {
          projectId,
          filter: { ...registerFilter },
        },
      });
    }
  }, [registerFilter]);

  const infiniteScrollObserverRef = useCallback(
    (node) => {
      if (loading) return;

      if (observerRef?.current) {
        observerRef?.current?.disconnect();
      }
      // eslint-disable-next-line no-undef
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) {
          if (registerData?.length >= totalCount) return;
          setScrollFlag(true);
          setRegisterFilter((prev) => ({
            ...prev,
            skip: registerData?.length,
          }));
          getAllRegisters({
            variables: {
              projectId,
              filter: {
                ...registerFilter,
                skip: registerData?.length,
              },
            },
          });
        }
      });

      if (node) observerRef?.current?.observe(node);
    },
    [registerData],
  );

  const items = map(registerData, (register, index) => {
    const isLastItem = index === registerData?.length - 1;
    return {
      label: (
        <div ref={isLastItem && hasMore ? infiniteScrollObserverRef : null}>
          <EllipsisText text={register?.name} heightRows={2} />
        </div>
      ),

      key: register?.id,
      children: (
        <RegisterDetails
          registerId={register?.id}
          registerFilter={registerFilter}
        />
      ),
    };
  });

  const onSearchChange = async (value) => {
    const newFilter = { ...registerFilter, skip: 0, search: value };
    setRegisterFilter(newFilter);
    const { data } = await getAllRegisters({
      variables: {
        projectId,
        filter: newFilter,
      },
    });

    if (data?.registerList?.data?.length > 0) {
      navigate(
        `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${data?.registerList?.data[0]?.id}`,
        {
          state: {
            registerNavFilter: newFilter,
            searchValue: value,
          },
        },
      );
    }
  };

  return (
    <div className="register-details">
      {includes(
        currentUser?.tenantUser?.tenant?.access,
        ACCESS_TYPE.REGISTER,
      ) ? (
        <>
          <div
            className={clsx(
              isDesktopViewport && 'd-flex justify-between align-center',
            )}
          >
            <div className="d-flex align-center mb-10">
              <div>
                <h2>Registers</h2>
                <div className="mt-15 data-count">
                  {totalCount}
                  {totalCount <= 1 ? ` Register` : ` Registers`}
                </div>
              </div>
            </div>
            <div className={clsx(isDesktopViewport && 'd-flex align-center')}>
              <SearchComponent
                className={`${isDesktopViewport ? 'width-200' : 'width-percent-100'} mb-10`}
                getData={onSearchChange}
                defaultValue={registerNavFilter?.search}
              />
            </div>
          </div>
          <div className="height-percent-100">
            {registerData?.length === 0 && !loading ? (
              <div>
                <Empty />
              </div>
            ) : (
              <Tabs
                onChange={(key) => {
                  navigate(
                    `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${key}`,
                    {
                      state: {
                        registerNavFilter: registerFilter,
                      },
                    },
                  );
                }}
                activeKey={registerId}
                className="register-tabs"
                destroyInactiveTabPane
                items={items}
                renderTabBar={(props, DefaultTabBar) => {
                  return (
                    <div className="overflow-x-auto overflow-y-hidden hide-scrollbar">
                      <DefaultTabBar {...props} />
                    </div>
                  );
                }}
              />
            )}
          </div>
          {showModal && (
            <AddRegisterModal
              showModal={showModal}
              setShowModal={setShowModal}
            />
          )}
        </>
      ) : (
        <Result
          icon={<div className="coming-soon-icon">⏳</div>}
          title="Coming Soon! "
          subTitle="We're working on something amazing! Stay tuned for updates."
        />
      )}
    </div>
  );
};

export default Register;
